<template>
  <section class="action-panel" :class="{ dark: isDark }">
    <div class="action-panel-header">
      <div>
        <div class="eyebrow">Operations</div>
        <h2>Action Required</h2>
      </div>
      <div class="status-pill" :class="{ clear: !loading && !items.length, pending: loading }">
        <i class="material-icons">{{ loading ? 'hourglass_empty' : items.length ? 'priority_high' : 'check_circle' }}</i>
        <span>{{ statusText }}</span>
      </div>
    </div>

    <div v-if="loading" class="checking-state">
      <div class="skeleton-line wide"></div>
      <div class="skeleton-line"></div>
    </div>

    <div v-else-if="items.length" class="action-list">
      <router-link
        v-for="item in items"
        :key="item.id"
        :to="item.to"
        class="action-item"
        :class="item.severity"
      >
        <div class="severity-marker" />
	        <div class="action-copy">
	          <div class="action-title-row">
	            <h3>{{ item.title }}</h3>
	            <span class="severity-label">{{ item.label }}</span>
	          </div>
	          <p>{{ item.description }}</p>
	          <div v-if="item.details && item.details.length" class="detail-list">
	            <span
	              v-for="detail in item.details"
	              :key="detail"
	              class="detail-chip"
	            >
	              {{ detail }}
	            </span>
	          </div>
	        </div>
        <span class="action-link">
          {{ item.cta }}
          <i class="material-icons">arrow_forward</i>
        </span>
      </router-link>
    </div>

    <div v-else class="clear-state">
      <div class="clear-icon">
        <i class="material-icons">done_all</i>
      </div>
      <div>
        <h3>No urgent exceptions for this period</h3>
        <p>AGCO, store assignment, product naming, and revenue-trend checks are clear based on the dashboard data that loaded.</p>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "ActionRequiredPanel",
  props: {
    items: {
      type: Array,
      default: () => []
    },
    isDark: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    statusText() {
      if (this.loading) return "Checking";
      if (!this.items.length) return "Clear";
      return `${this.items.length} ${this.items.length === 1 ? 'issue' : 'issues'}`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";

.action-panel {
  background: $lightCardBg;
  border: 1px solid $lightBorderColor;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;

  &.dark {
    background: $darkCardBg;
    border-color: $darkBorderColor;
    color: $darkTextColor;
  }
}

.action-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;

  h2 {
    margin: 2px 0 0;
    color: $primaryColor;
    font-size: 18px;
    font-weight: 700;

    .dark & {
      color: $secondaryColorDark;
    }
  }
}

.eyebrow {
  color: $textColor;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  opacity: 0.55;
  text-transform: uppercase;

  .dark & {
    color: $darkTextColor;
  }
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 30px;
  padding: 0 10px;
  border-radius: 999px;
  background: rgba($dangerColor, 0.1);
  color: $dangerColor;
  font-size: 12px;
  font-weight: 700;

  i {
    font-size: 16px;
  }

  &.clear {
    background: rgba($successColor, 0.1);
    color: darken($successColor, 12%);
  }

  &.pending {
    background: rgba($warningColor, 0.12);
    color: darken($warningColor, 20%);
  }
}

.action-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
}

.checking-state {
  display: grid;
  gap: 10px;
  min-height: 78px;
  padding: 12px;
  border: 1px solid $lightBorderColor;
  border-radius: 10px;

  .dark & {
    border-color: $darkBorderColor;
  }
}

.skeleton-line {
  height: 14px;
  max-width: 360px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba($textColor, 0.08), rgba($textColor, 0.15), rgba($textColor, 0.08));
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;

  &.wide {
    max-width: 520px;
  }

  .dark & {
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.14), rgba(255, 255, 255, 0.06));
    background-size: 200% 100%;
  }
}

.action-item {
  display: grid;
  grid-template-columns: 4px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  min-height: 86px;
  padding: 12px;
  border: 1px solid $lightBorderColor;
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  transition: border-color 0.2s ease, transform 0.2s ease, background-color 0.2s ease;

  &:hover {
    background: $lightHoverColor;
    border-color: rgba($primaryColor, 0.2);
    transform: translateY(-1px);
  }

  &.high .severity-marker {
    background: $dangerColor;
  }

  &.medium .severity-marker {
    background: $warningColor;
  }

  &.low .severity-marker {
    background: $infoColor;
  }

  .dark & {
    border-color: $darkBorderColor;

    &:hover {
      background: rgba(255, 255, 255, 0.04);
      border-color: rgba($secondaryColorDark, 0.35);
    }
  }
}

.severity-marker {
  width: 4px;
  height: 100%;
  min-height: 54px;
  border-radius: 999px;
}

.action-copy {
  min-width: 0;

  h3 {
    margin: 0;
    color: $primaryColor;
    font-size: 14px;
    font-weight: 700;

    .dark & {
      color: $darkTextColor;
    }
  }

	  p {
	    margin: 5px 0 0;
	    color: $textColor;
	    font-size: 12px;
	    line-height: 1.4;
	    opacity: 0.75;

	    .dark & {
	      color: $darkTextColor;
	    }
	  }
	}

	.detail-list {
	  display: flex;
	  flex-wrap: wrap;
	  gap: 6px;
	  margin-top: 8px;
	}

	.detail-chip {
	  display: inline-flex;
	  align-items: center;
	  max-width: 100%;
	  min-height: 22px;
	  padding: 3px 7px;
	  border: 1px solid rgba($textColor, 0.1);
	  border-radius: 6px;
	  background: rgba($textColor, 0.04);
	  color: $textColor;
	  font-size: 11px;
	  font-weight: 600;
	  line-height: 1.2;

	  .dark & {
	    border-color: rgba(255, 255, 255, 0.1);
	    background: rgba(255, 255, 255, 0.05);
	    color: $darkTextColor;
	  }
	}

.action-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.severity-label {
  flex: 0 0 auto;
  padding: 2px 7px;
  border-radius: 999px;
  background: rgba($textColor, 0.07);
  color: $textColor;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;

  .dark & {
    background: rgba(255, 255, 255, 0.08);
    color: $darkTextColor;
  }
}

.action-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: $primaryColor;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;

  i {
    font-size: 16px;
  }

  .dark & {
    color: $secondaryColorDark;
  }
}

.clear-state {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 78px;
  padding: 12px;
  border: 1px dashed $lightBorderColor;
  border-radius: 10px;

  .dark & {
    border-color: $darkBorderColor;
  }

  h3 {
    margin: 0;
    color: $primaryColor;
    font-size: 14px;
    font-weight: 700;

    .dark & {
      color: $darkTextColor;
    }
  }

  p {
    margin: 4px 0 0;
    color: $textColor;
    font-size: 12px;
    opacity: 0.7;

    .dark & {
      color: $darkTextColor;
    }
  }
}

.clear-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba($successColor, 0.12);
  color: darken($successColor, 12%);

  i {
    font-size: 20px;
  }
}

@keyframes shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}
</style>
